import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { getCampaignDetail } from 'src/apis/campaign';
import useAuth from 'src/hooks/useAuth';
import useCampaign from 'src/hooks/useCampaign';
import {
  campaignPath,
  canAccessCampaigns,
  getDefaultAuthenticatedRoute,
  ROUTES,
} from 'src/utils';

/**
 * Keeps the open campaign in the address bar as `/campaigns/:id`.
 * A linked id the organization cannot see shows the no-access notice.
 */
export default function useCampaignLink() {
  const history = useHistory();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const linkedId = campaignId ?? null;
  const { organization } = useAuth();
  const { selectedCampaignId, setSelectedCampaignId } = useCampaign();
  const [denied, setDenied] = useState(false);
  const [pending, setPending] = useState(false);
  const prevPathId = useRef<string | null>(null);
  const prevSelectedId = useRef<string | null>(null);
  const ready = Boolean(organization);
  const canAccess = canAccessCampaigns(organization?.permission);

  useEffect(() => {
    if (!ready) return;

    let active = true;

    if (linkedId && !canAccess) {
      setPending(false);
      setDenied(true);
      return;
    }

    const pathChanged = linkedId !== prevPathId.current;
    const selectionChanged = selectedCampaignId !== prevSelectedId.current;

    if (
      pathChanged &&
      selectionChanged &&
      linkedId &&
      linkedId === selectedCampaignId
    ) {
      prevPathId.current = linkedId;
      prevSelectedId.current = selectedCampaignId;
      setDenied(false);
      setPending(false);
      return;
    }

    if (linkedId && pathChanged && !selectionChanged) {
      prevPathId.current = linkedId;

      if (selectedCampaignId === linkedId) {
        setDenied(false);
        setPending(false);
        return;
      }

      setPending(true);
      setDenied(false);
      getCampaignDetail(linkedId)
        .then(() => {
          if (!active) return;
          setPending(false);
          setDenied(false);
          setSelectedCampaignId(linkedId);
          prevSelectedId.current = linkedId;
        })
        .catch((error: { response?: { status?: number } }) => {
          if (!active) return;
          setPending(false);
          const status = error?.response?.status;
          if (status === 401 || status === 408) return;
          if (!status || status === 403 || status === 404) {
            setDenied(true);
            return;
          }
          setSelectedCampaignId(linkedId);
          prevSelectedId.current = linkedId;
        });

      return () => {
        active = false;
      };
    }

    if (selectionChanged) {
      prevSelectedId.current = selectedCampaignId;
      setPending(false);
      if (selectedCampaignId && selectedCampaignId !== linkedId && canAccess) {
        prevPathId.current = selectedCampaignId;
        setDenied(false);
        if (linkedId) {
          history.push(campaignPath(selectedCampaignId));
        } else {
          history.replace(campaignPath(selectedCampaignId));
        }
      } else if (!selectedCampaignId && linkedId) {
        prevPathId.current = null;
        setDenied(false);
        history.replace(ROUTES.CAMPAIGNS);
      }
      return;
    }

    if (!linkedId && selectedCampaignId && canAccess && !denied) {
      prevPathId.current = selectedCampaignId;
      prevSelectedId.current = selectedCampaignId;
      history.replace(campaignPath(selectedCampaignId));
      return;
    }

    if (!linkedId) {
      setDenied(false);
      setPending(false);
    }

    return () => {
      active = false;
    };
  }, [
    ready,
    linkedId,
    selectedCampaignId,
    canAccess,
    denied,
    history,
    setSelectedCampaignId,
  ]);

  const redirectTo = canAccess
    ? ROUTES.CAMPAIGNS
    : getDefaultAuthenticatedRoute(organization?.permission);

  return {
    denied: denied && !!linkedId,
    pending: pending && !!linkedId && !denied,
    redirectTo,
  };
}
